import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Modale du BO : voile sombre, carte au thème, fermeture par Échap, clic sur le voile ou la croix.
 * Le focus va dans la modale à l'ouverture et revient sur l'élément d'origine à la fermeture.
 */
export default function Modal({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-md rounded-lg border border-card-border bg-surface-neutral shadow-elevation-3 outline-none"
      >
        <header className="flex items-center justify-between gap-3 border-b border-card-border px-4 py-3">
          <h2 id={titleId} className="text-lg font-bold text-white">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            className="rounded p-1 text-outline hover:bg-white/10 hover:text-white"
          >
            <X size={18} aria-hidden />
          </button>
        </header>
        <div className="p-4">{children}</div>
        {footer ? (
          <footer className="flex justify-end gap-2 border-t border-card-border px-4 py-3">{footer}</footer>
        ) : null}
      </div>
    </div>
  );
}
